import {keepPreviousData, useQuery} from '@tanstack/react-query';
import type {ScopeType, WorkstreamNavNode} from 'client';
import {WorkstreamListService} from 'client';
import {queryClient} from 'views/QueryClientWrapper';
import type {WorkstreamFilters} from 'views/workstreams/detail/types';

// Prefix of every rail nav-tree query, for invalidating them all.
export const NAV_TREE_QUERY_PREFIX = ['prototype-nav-tree'] as const;

/**
 * fetch the lightweight left-rail nav tree for /workstreams.
 *
 * This is intentionally decoupled from `useWorkstreams` (which drives the heavy
 * right-pane table/detail): the outline only needs id/name/pillar/parent-edges/
 * status, so it comes from the cheap `/prototype/nav-tree` endpoint in a single
 * request and is cached aggressively (the hierarchy rarely changes within a
 * session). Only `filters.timeframes` is honored server-side.
 */
export const useNavTree = ({
  scope_type,
  scope_value,
  filters,
  enabled = true,
}: {
  scope_type: string;
  scope_value?: string | null;
  filters: WorkstreamFilters;
  enabled?: boolean;
}) => {
  const timeframes = filters.timeframes ?? [];
  const filtersParam = timeframes.length > 0 ? JSON.stringify({timeframes}) : undefined;

  const query = useQuery<WorkstreamNavNode[]>({
    queryKey: [...NAV_TREE_QUERY_PREFIX, scope_type, scope_value ?? null, filtersParam ?? null],
    queryFn: async () => {
      const res = await WorkstreamListService.getPrototypeNavTreeApiV1WorkstreamPrototypeNavTreeGet(
        scope_type as ScopeType,
        scope_value ?? undefined,
        filtersParam
      );
      return res.nodes ?? [];
    },
    enabled,
    // A new timeframe keeps the rail it replaces until it lands, rather than
    // blanking it to a skeleton (isPlaceholderData says it's the old one).
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return query;
};

/**
 * Refetch every rail tree. Their 5-minute staleTime (and own query key) means
 * workstream edits don't reach the rail on their own, so edits that change its
 * outline (linking, unlinking, reordering sub-workstreams) call this.
 */
export const invalidateNavTrees = () =>
  queryClient.invalidateQueries({queryKey: ['prototype-nav-tree']});
